import {
  Card, EmptyState, ErrorState, LARGEST_PAGE, LoadingState, MedicationDose, PageHeader, Select, Stack,
  useApi, useI18n,
  type MedicationResponse,
} from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useMyClasses } from '../myClasses';

/**
 * The medication round (FR-CHD-16, FR-CHD-17): what a class's children have in force today, and a dose recorded
 * against each, for holders of `medication.administer`.
 *
 * One request for the class, not one per child, on the connections this app is built for. Online only: a dose tells
 * the family at once, and whoever gives it must know it was recorded before the next one is due.
 */
export function MedicationRound() {
  const api = useApi();
  const { t } = useI18n();
  const { classes, mine, opening } = useMyClasses();
  const [classId, setClassId] = useState('');
  const active = classId || (opening ? String(opening.id) : '');

  const due = useQuery({
    queryKey: ['medications-due', active],
    queryFn: () => api.health.dueForClass(Number(active), { pageSize: LARGEST_PAGE }),
    enabled: active !== '',
  });

  if (classes.isPending) return <LoadingState />;
  if (classes.isError) return <ErrorState error={classes.error} onRetry={() => void classes.refetch()} />;
  if (mine.length === 0) return <EmptyState icon="🧩" title={t('class.none')} />;

  const byChild = new Map<number, MedicationResponse[]>();
  for (const medication of due.data?.items ?? []) {
    byChild.set(medication.childId, [...(byChild.get(medication.childId) ?? []), medication]);
  }

  return (
    <Stack>
      <PageHeader title={t('medication.dueTitle')} />

      {mine.length > 1 && (
        <Card>
          <label className="dg-field">
            <span className="dg-field__label">{t('class.switch')}</span>
            <Select value={active} onChange={(event) => setClassId(event.target.value)}>
              <option value="">—</option>
              {mine.map((klass) => <option key={klass.id} value={klass.id}>{klass.name}</option>)}
            </Select>
          </label>
        </Card>
      )}

      {active === '' && <EmptyState icon="🏫" title={t('class.switch')} />}
      {due.isPending && active !== '' && <LoadingState />}
      {due.isError && <ErrorState error={due.error} onRetry={() => void due.refetch()} />}
      {due.data?.items.length === 0 && <EmptyState icon="💊" title={t('medication.dueEmpty')} />}

      {[...byChild.values()].map((medications) => (
        <Card key={medications[0]!.childId} title={medications[0]!.childName}>
          <Stack>
            {medications.map((medication) => <MedicationDose key={medication.id} medication={medication} />)}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
